import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Divider, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { Flex } from "Components/_Shared/Core/Flex";
import type { CaseInvestmentStrategy } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatPercentage } from "Infrastructure/Utils/NumberUtils";
import { formatSrriSri } from "./utils";

type Props = {
  readonly portfolio: CaseInvestmentStrategy;
  readonly selected: boolean;
  readonly onClick: () => void;
  readonly disabled?: boolean;
};

export const InvestmentsNsurePortfolioCard = ({ portfolio, selected, onClick, disabled = false }: Props) => {
  const { t, resolveResourcePath } = useResource();
  return (
    <Box
      onClick={disabled ? undefined : onClick}
      px={Spacing.XL}
      py={Spacing.L}
      bgcolor="background.paper"
      border="2px solid"
      borderColor={selected ? "primary.main" : "divider"}
      borderRadius="14px"
      sx={{
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.5 : 1,
        boxShadow: 1,
        transition: "box-shadow 0.15s, border-color 0.15s, transform 0.15s",
        ...(!disabled && {
          "&:hover": {
            borderColor: "primary.main",
            boxShadow: 3,
            transform: "scale(1.01)",
          },
        }),
      }}
    >
      <Stack spacing={Spacing.M}>
        <Box>
          <Flex justifyContent="space-between" alignItems="center" minHeight="30px">
            <Typography variant="body1" fontWeight={600} color="primary.main">
              {portfolio.name}
            </Typography>
            {selected ? (
              <CheckCircleIcon sx={{ color: "primary.main", fontSize: 20, m: "5px" }} />
            ) : (
              portfolio.description && (
                <Tooltip
                  title={`${t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.PortfolioCard.Description)}: ${portfolio.description}`}
                  placement="top"
                  arrow
                  disableHoverListener={!portfolio.description}
                >
                  <IconButton size="small" onClick={(e) => e.stopPropagation()}>
                    <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  </IconButton>
                </Tooltip>
              )
            )}
          </Flex>
          <Typography variant="caption" color="text.secondary">
            {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.PortfolioCard.Category)}{" "}
            {t(resolveResourcePath(`Resources.AnalysisDetail.GoalsFulfillment.Nsure.StrategyRiskType.${portfolio.strategyRiskType}`))}
          </Typography>
        </Box>
        <Divider />
        <Flex gap={Spacing.XL} pt={Spacing.S}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.SrriSri)}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {formatSrriSri(portfolio.srri, portfolio.sri)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.Esg)}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {formatPercentage(portfolio.esg ?? 0)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.ExpectedPerformance)}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {formatPercentage(portfolio.expectedPerformance ?? 0)}
            </Typography>
          </Box>
        </Flex>
      </Stack>
    </Box>
  );
};
